<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PatientsAPI from 'dashboard/api/patients';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const patients = ref([]);
const isFetching = ref(false);
const globalConfig = useMapGetter('globalConfig/get');

const clientId = computed(() => props.selectedContact?.clientId || props.selectedContact?.client_id);

const fetchPatients = async () => {
  if (!clientId.value) {
    patients.value = [];
    return;
  }

  isFetching.value = true;
  try {
    const response = await PatientsAPI.get(1, 'name', clientId.value);
    patients.value = response.data.payload || [];
  } catch (error) {
    console.error('Error fetching patients:', error);
    patients.value = [];
  } finally {
    isFetching.value = false;
  }
};

const formatDate = date => {
  if (!date) return '';
  const d = new Date(date * 1000);
  // Format date in UK format DD/MM/YYYY
  return d.toLocaleDateString('en-GB');
};

const getProvetPatientUrl = (patientId) => {
  if (!patientId) return null;
  const provetBaseUrl =
    globalConfig.value?.provetBaseUrl || window.globalConfig?.PROVET_BASE_URL;
  if (!provetBaseUrl) return null;
  return `${provetBaseUrl}/patient/${patientId}/`;
};

const handlePatientNameClick = (patientId) => {
  const url = getProvetPatientUrl(patientId);
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

watch(clientId, () => {
  fetchPatients();
});

onMounted(() => {
  fetchPatients();
});
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="!clientId" class="px-6 py-10">
    <p class="text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.PATIENTS.NO_CLIENT') }}
    </p>
  </div>
  <div
    v-else-if="patients.length > 0"
    class="px-6 py-4 divide-y divide-n-strong"
  >
    <div
      v-for="patient in patients"
      :key="patient.id"
      class="py-4 first:pt-0 last:pb-0"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <button
            @click="handlePatientNameClick(patient.id)"
            class="font-medium text-n-slate-12 hover:text-n-brand cursor-pointer hover:underline"
          >
            {{ patient.name }}
          </button>
          <span
            v-if="patient.species"
            class="px-2 py-0.5 text-xs rounded bg-n-slate-4 text-n-slate-11"
          >
            {{ patient.species }}
          </span>
        </div>
        <div class="flex flex-wrap gap-3 text-sm text-n-slate-11">
          <span v-if="patient.breed">{{ patient.breed }}</span>
          <span v-if="patient.gender && patient.gender !== 'unknown'">
            {{ patient.gender }}
          </span>
          <span v-if="patient.dateOfBirth || patient.date_of_birth">
            {{ formatDate(patient.dateOfBirth || patient.date_of_birth) }}
          </span>
        </div>
      </div>
    </div>
  </div>
  <p v-else class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11">
    {{ t('CONTACTS_LAYOUT.SIDEBAR.PATIENTS.EMPTY_STATE') }}
  </p>
</template>

